import React, { useState, useEffect, useReducer, useContext, useCallback, useRef } from "react";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import BlockIcon from "@material-ui/icons/Block";
import CheckIcon from "@material-ui/icons/Check";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ReceiptOutlinedIcon from "@material-ui/icons/ReceiptOutlined";
import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import {
  fmtBRL,
  useFinanceiroStyles,
  KpiStrip,
  PeriodFilter,
  defaultPeriod,
  periodTitle,
  effectiveStatus,
  StatusPill,
  relativeDue,
  downloadCsv,
} from "./financeiroShared";

const txReducer = (state, action) => {
  if (action.type === "SET") return action.payload;
  return state;
};

const STATUS_LABEL = { pending: "Pendente", paid: "Pago", cancelled: "Cancelado" };

const CaixaPanel = ({ onEdit, reloadKey, registerExport, onSubtitle }) => {
  const classes = useFinanceiroStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);

  const [transactions, dispatch] = useReducer(txReducer, []);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const [period, setPeriod] = useState(defaultPeriod);
  const [searchInput, setSearchInput] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, tx: null });

  const dateFrom = period.from;
  const dateTo = period.to;
  const isValidRange = !dateFrom || !dateTo || dateFrom <= dateTo;

  // Debounce da busca.
  useEffect(() => {
    const t = setTimeout(() => setSearchParam(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const { data } = await api.get("/transactions/dashboard", {
        params: { dateFrom, dateTo },
      });
      setDashboard(data);
    } catch (err) {
      toastError(err);
    } finally {
      setDashLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadTransactions = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const { data } = await api.get("/transactions", {
          params: {
            dateFrom,
            dateTo,
            type: filterType || undefined,
            status: filterStatus || undefined,
            searchParam: searchParam || undefined,
            pageNumber: page,
          },
        });
        dispatch({ type: "SET", payload: data.transactions });
        setHasMore(data.hasMore);
        setCount(data.count);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [dateFrom, dateTo, filterType, filterStatus, searchParam]
  );

  // Filtro mudou: volta para a primeira página.
  useEffect(() => {
    setPageNumber(1);
  }, [dateFrom, dateTo, filterType, filterStatus, searchParam]);

  useEffect(() => {
    if (!isValidRange) {
      toast.warn("Período inválido: a data inicial é maior que a final.", {
        toastId: "invalid-range",
      });
      return;
    }
    loadTransactions(pageNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTransactions, pageNumber, reloadKey]);

  useEffect(() => {
    if (!isValidRange) return;
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDashboard, reloadKey]);

  // Refs para o socket sempre usar os filtros/página atuais sem reconectar.
  const reloadRef = useRef(() => {});
  useEffect(() => {
    reloadRef.current = () => {
      loadDashboard();
      loadTransactions(pageNumber);
    };
  }, [loadDashboard, loadTransactions, pageNumber]);

  useEffect(() => {
    const socket = socketConnection({ companyId: user.companyId });
    socket.on("transaction", () => reloadRef.current());
    return () => socket.disconnect();
  }, [user.companyId]);

  // Subtítulo do cabeçalho.
  useEffect(() => {
    onSubtitle(
      `${periodTitle(period.key, dateFrom, dateTo)} · ${count} lançamento${count === 1 ? "" : "s"}`
    );
  }, [onSubtitle, period.key, dateFrom, dateTo, count]);

  // Exportação CSV com os filtros ativos (todas as páginas).
  const handleExport = useCallback(async () => {
    try {
      toast.info("Exportando lançamentos...");
      const { data } = await api.get("/transactions", {
        params: {
          dateFrom,
          dateTo,
          type: filterType || undefined,
          status: filterStatus || undefined,
          searchParam: searchParam || undefined,
          all: true,
        },
      });
      const rows = data.transactions.map((tx) => {
        const st = effectiveStatus(tx);
        return [
          tx.description,
          tx.contact?.name || "",
          tx.category?.name || "",
          tx.type === "income" ? "Receita" : "Despesa",
          Number(tx.value).toFixed(2).replace(".", ","),
          moment(tx.dueDate).format("DD/MM/YYYY"),
          st === "overdue" ? "Vencido" : STATUS_LABEL[st] || st,
          tx.paidAt ? moment(tx.paidAt).format("DD/MM/YYYY HH:mm") : "",
          tx.paymentMethod?.name || "",
          tx.notes || "",
        ];
      });
      downloadCsv(
        `financeiro-${dateFrom}-a-${dateTo}.csv`,
        ["Descrição", "Contato", "Categoria", "Tipo", "Valor", "Vencimento", "Status", "Pago em", "Forma de pagamento", "Observações"],
        rows
      );
      if (rows.length === 0) toast.warn("Nenhum lançamento no período.");
    } catch (err) {
      toastError(err);
    }
  }, [dateFrom, dateTo, filterType, filterStatus, searchParam]);

  useEffect(() => {
    registerExport(handleExport);
    return () => registerExport(null);
  }, [registerExport, handleExport]);

  const handleMarkPaid = async (tx) => {
    try {
      await api.put(`/transactions/${tx.id}`, {
        status: "paid",
        paidAt: moment().format("YYYY-MM-DDTHH:mm"),
      });
      toast.success("Lançamento marcado como pago.");
      reloadRef.current();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCancel = async () => {
    const tx = cancelConfirm;
    setCancelConfirm(null);
    try {
      await api.delete(`/transactions/${tx.id}`);
      reloadRef.current();
      toast.success("Lançamento cancelado.");
    } catch (err) {
      toastError(err);
    }
  };

  // ── Indicadores ─────────────────────────────────────────────────────────
  const balance = dashboard?.balance ?? 0;
  let balanceCaption;
  if (dashboard?.previousHasData && dashboard.previousBalance !== 0) {
    const pct = ((balance - dashboard.previousBalance) / Math.abs(dashboard.previousBalance)) * 100;
    balanceCaption = `${pct >= 0 ? "+" : "−"}${Math.abs(Math.round(pct))}% vs. período anterior`;
  }
  const kpis = [
    {
      label: "Receitas pagas",
      value: fmtBRL(dashboard?.totalIncome),
      tone: "green",
    },
    {
      label: "Despesas pagas",
      value: fmtBRL(dashboard?.totalExpense),
      tone: "red",
    },
    {
      label: "Saldo do período",
      value: fmtBRL(balance),
      tone: balance >= 0 ? "blue" : "red",
      colorValue: true,
      caption: balanceCaption,
    },
    {
      label: "A receber",
      value: fmtBRL(dashboard?.pendingIncome),
      tone: "amber",
      caption: dashboard?.overdueIncome > 0 ? `${fmtBRL(dashboard.overdueIncome)} vencidos` : undefined,
      captionTone: "red",
    },
  ];

  const canPay = (tx) => ["pending", "overdue"].includes(effectiveStatus(tx));
  const canEdit = (tx) => tx.status !== "cancelled";

  const categoryCell = (tx) =>
    tx.category ? (
      <>
        <span className={classes.categoryDot} style={{ backgroundColor: tx.category.color }} />
        {tx.category.name}
      </>
    ) : (
      <span className={classes.muted}>—</span>
    );

  const valueText = (tx) =>
    `${tx.type === "income" ? "+" : "−"} ${fmtBRL(tx.value)}`;

  const statusPill = (tx) => {
    const st = effectiveStatus(tx);
    const label =
      st === "paid" && tx.paidAt ? `Pago ${moment(tx.paidAt).format("DD/MM")}` : undefined;
    return <StatusPill status={st} label={label} classes={classes} />;
  };

  const emptyMessage =
    searchParam || filterType || filterStatus
      ? "Nenhum lançamento encontrado com esses filtros."
      : "Nenhum lançamento neste período.";

  const toolbar = (
    <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
      <TextField
        className={classes.searchField}
        placeholder="Buscar por descrição ou contato"
        type="search"
        variant="outlined"
        size="small"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        inputProps={{ "aria-label": "Buscar lançamentos" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: theme.palette.text.secondary }} />
            </InputAdornment>
          ),
        }}
      />
      <PeriodFilter
        value={period}
        onChange={setPeriod}
        classes={classes}
        scroll={isMobile}
        periods={undefined}
      />
      {!isMobile && <span style={{ flex: 1 }} />}
      <div className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`} role="group" aria-label="Tipo">
        {[
          ["", "Todos"],
          ["income", "Receitas"],
          ["expense", "Despesas"],
        ].map(([v, l]) => (
          <Chip
            key={v || "all"}
            clickable
            className={classes.filterChip}
            label={l}
            color={filterType === v ? "primary" : "default"}
            variant={filterType === v ? "default" : "outlined"}
            aria-pressed={filterType === v}
            onClick={() => setFilterType(v)}
          />
        ))}
      </div>
      <Select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        variant="outlined"
        displayEmpty
        margin="dense"
        style={{ height: 34, borderRadius: 10, fontSize: 13 }}
        inputProps={{ "aria-label": "Status" }}
      >
        <MenuItem value="">Status: todos</MenuItem>
        <MenuItem value="pending">Pendente</MenuItem>
        <MenuItem value="paid">Pago</MenuItem>
        <MenuItem value="cancelled">Cancelado</MenuItem>
      </Select>
    </div>
  );

  return (
    <div className={classes.root}>
      <Dialog open={!!cancelConfirm} onClose={() => setCancelConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar lançamento?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O lançamento <strong>"{cancelConfirm?.description}"</strong> sai das listas e dos
            totais, mas permanece no histórico (filtre por status "Cancelado" para consultar).
            Não é possível desfazer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirm(null)}>Voltar</Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            disableElevation
            style={{ backgroundColor: theme.palette.error.main, color: "#fff" }}
          >
            Cancelar lançamento
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menu.anchor}
        open={!!menu.anchor}
        onClose={() => setMenu({ anchor: null, tx: null })}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menu.tx && canPay(menu.tx) && (
          <MenuItem
            onClick={() => {
              const tx = menu.tx;
              setMenu({ anchor: null, tx: null });
              handleMarkPaid(tx);
            }}
          >
            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Marcar como pago" />
          </MenuItem>
        )}
        {menu.tx && canEdit(menu.tx) && (
          <MenuItem
            onClick={() => {
              const tx = menu.tx;
              setMenu({ anchor: null, tx: null });
              onEdit(tx);
            }}
          >
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Editar" />
          </MenuItem>
        )}
        {menu.tx && canEdit(menu.tx) && (
          <MenuItem
            style={{ color: theme.palette.error.main }}
            onClick={() => {
              const tx = menu.tx;
              setMenu({ anchor: null, tx: null });
              setCancelConfirm(tx);
            }}
          >
            <ListItemIcon style={{ color: "inherit" }}><BlockIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Cancelar lançamento" />
          </MenuItem>
        )}
      </Menu>

      <KpiStrip items={kpis} classes={classes} theme={theme} loading={dashLoading} />

      <Paper className={classes.mainPaper} variant="outlined">
        {toolbar}

        <div className={classes.tableWrapper}>
          {isMobile ? (
            <div>
              {!loading && transactions.length === 0 && (
                <div className={classes.emptyState}>
                  <ReceiptOutlinedIcon className={classes.emptyIcon} />
                  <Typography variant="body2">{emptyMessage}</Typography>
                </div>
              )}
              {transactions.map((tx) => {
                const st = effectiveStatus(tx);
                return (
                  <div
                    key={tx.id}
                    className={`${classes.mobileRow} ${st === "overdue" ? classes.lateRow : ""}`}
                  >
                    <div className={classes.mobileInfo}>
                      <div className={`${classes.mobileTitle} ${st === "cancelled" ? classes.cancelledText : ""}`}>
                        {tx.description}
                      </div>
                      <div className={classes.subtle}>
                        {tx.category && (
                          <span className={classes.categoryDot} style={{ backgroundColor: tx.category.color, width: 8, height: 8 }} />
                        )}
                        {[tx.category?.name, `${st === "paid" ? "pago" : "vence"} ${moment(st === "paid" && tx.paidAt ? tx.paidAt : tx.dueDate).format("DD/MM")}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <div className={classes.mobileValue}>
                      <span className={tx.type === "income" ? classes.income : classes.expense}>
                        {valueText(tx)}
                      </span>
                      <StatusPill status={st} classes={classes} />
                    </div>
                    {canEdit(tx) && (
                      <IconButton
                        size="small"
                        className={classes.iconAction}
                        aria-label="Mais ações"
                        onClick={(e) => setMenu({ anchor: e.currentTarget, tx })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                );
              })}
              {loading && transactions.length === 0 && (
                <Typography align="center" className={classes.muted} style={{ padding: 24 }}>
                  Carregando...
                </Typography>
              )}
            </div>
          ) : (
            <Table stickyHeader size="small" aria-label="lançamentos" style={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Descrição</TableCell>
                  <TableCell className={classes.headCell}>Categoria</TableCell>
                  <TableCell className={classes.headCell} align="right">Valor</TableCell>
                  <TableCell className={classes.headCell}>Vencimento</TableCell>
                  <TableCell className={classes.headCell}>Status</TableCell>
                  <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => {
                  const st = effectiveStatus(tx);
                  return (
                    <TableRow key={tx.id} className={`${classes.row} ${st === "overdue" ? classes.lateRow : ""}`}>
                      <TableCell style={{ paddingLeft: 20 }}>
                        <div className={st === "cancelled" ? classes.cancelledText : undefined}>
                          <div style={{ fontWeight: 600 }}>{tx.description}</div>
                          {(tx.contact || tx.paymentMethod) && (
                            <div className={classes.subtle}>
                              {[tx.contact?.name, tx.paymentMethod?.name].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{categoryCell(tx)}</TableCell>
                      <TableCell align="right">
                        <span className={tx.type === "income" ? classes.income : classes.expense}>
                          {valueText(tx)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {moment(tx.dueDate).format("DD/MM/YYYY")}
                        {st !== "paid" && st !== "cancelled" && (
                          <div className={st === "overdue" ? classes.lateText : classes.subtle}>
                            {relativeDue(tx.dueDate)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{statusPill(tx)}</TableCell>
                      <TableCell align="right" style={{ paddingRight: 20 }}>
                        <div className={classes.actions}>
                          {canPay(tx) && (
                            <Button
                              size="small"
                              className={classes.payButton}
                              startIcon={<CheckIcon style={{ fontSize: 16 }} />}
                              onClick={() => handleMarkPaid(tx)}
                            >
                              Marcar pago
                            </Button>
                          )}
                          {canEdit(tx) && (
                            <>
                              <Tooltip title="Editar">
                                <IconButton size="small" className={classes.iconAction} aria-label="Editar lançamento" onClick={() => onEdit(tx)}>
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancelar lançamento">
                                <IconButton size="small" className={classes.deleteAction} aria-label="Cancelar lançamento" onClick={() => setCancelConfirm(tx)}>
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && transactions.length === 0 && <TableRowSkeleton columns={6} />}
                {!loading && transactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <div className={classes.emptyState}>
                        <ReceiptOutlinedIcon className={classes.emptyIcon} />
                        <Typography variant="body2">{emptyMessage}</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {count > 0 && (
          <div
            className={classes.footer}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          >
            <span>
              Mostrando {transactions.length} de {count} lançamento{count === 1 ? "" : "s"}
            </span>
            {(hasMore || pageNumber > 1) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Button size="small" variant="outlined" disabled={pageNumber === 1 || loading} onClick={() => setPageNumber((p) => p - 1)}>
                  ‹ Anterior
                </Button>
                <span>Página {pageNumber}</span>
                <Button size="small" variant="outlined" disabled={!hasMore || loading} onClick={() => setPageNumber((p) => p + 1)}>
                  Próxima ›
                </Button>
              </div>
            )}
          </div>
        )}
      </Paper>
    </div>
  );
};

export default CaixaPanel;
